<script setup>
import { datadogLogs } from "@datadog/browser-logs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { format as formatDate, parseISO } from "date-fns";
import { computed, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import ErrorPage from "@/components/errorPage.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import { ERROR_MESSAGE } from "@/components/StudentFulfillment/components/modals/HousingFulfillmentModal/constants";
import OrderWithdrawnModal from "@/components/StudentFulfillment/components/modals/OrderWithdrawnModal";
import PriceDifferenceModal from "@/components/StudentFulfillment/components/modals/PriceDifferenceModal";
import DetailSection from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/index";
import InvoiceSection from "@/components/StudentFulfillment/components/OrderDetail/sections/Invoice/index";
import NotesSection from "@/components/StudentFulfillment/components/OrderDetail/sections/Notes/index";
import SessionBasePrice from "@/components/StudentFulfillment/components/OrderDetail/sections/SessionBasePriceTotal";
import StudentInfo from "@/components/StudentFulfillment/components/OrderDetail/sections/StudentInfo";
import BreadCrumb from "@/components/shared/BreadCrumb";
import PlusIcon from "@/components/shared/icons/PlusIcon";
import ExpandingSection from "@/components/shared/section/ExpandingSection";
import {
  hasDisplayStudentDiscountEnabled,
  hasInvoiceFeatureEnabled,
  hasOrdersWriteAdminPermission,
  hasOrdersWriteFulfillPermission,
  hasOrdersWritePermission,
  hasUsersAdvisePermission,
  usePriceOverrides,
} from "@/composables/authorization";
import { useGetOrderSessionDiff } from "@/composables/useOrder.js";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "@/constants";
import { ORDER_STATUS } from "@/constants.js";
import { useGetSessionPricing } from "@/providers/sessions/useGetSessionPricing";
import ordersService from "@/services/orders";
import programSessionsService from "@/services/programSessions";
import { formattedPriceFromCentsWithoutCents } from "@/util/formatter";
import { validateOrderStatusAllowsPaidConfirmedOrFulfilled } from "@/util/orderConfirmationDepositValidation";

defineOptions({
  name: "OrderDetail",
});

const orderData = ref({});

const route = useRoute();
const toast = useToast();
const store = useStore();
const updateData = reactive({
  needs_advising: undefined,
  financially_cleared: undefined,
  requires_accommodations: undefined,
  financial_aid: [
    {
      using_financial_aid: undefined,
      certified_fafsa_eligible: undefined,
      eligible_for_member_scholarships: undefined,
    },
  ],
});
const disableButton = ref(
  !hasOrdersWriteFulfillPermission.value && !hasUsersAdvisePermission.value && !hasOrdersWriteAdminPermission.value,
);
const openAddEditNoteModal = ref(false);
const openPriceDiffModal = ref(false);
const openOrderWithdrawnModal = ref(false);
const initialOrderStatus = ref(undefined);

const hasInvoices = ref(false);

const queryClient = useQueryClient();

const getOneQueryKey = computed(() => [
  "order",
  route.params?.orderId,
  {
    get_canceled: true,
    get_withdrawn: true,
  },
]);

const orderPriceDiffQueryKey = computed(() => [
  "orderPriceDiff",
  orderSessionId.value,
  route.params?.orderId,
  { newProducts: [], forceRecalculate: true },
]);

const { isError: orderError, isLoading: isOrderLoading } = useQuery({
  queryKey: getOneQueryKey.value,
  queryFn: () =>
    ordersService
      .getOne({
        order_id: route.params?.orderId,
        params: {
          get_canceled: true,
          get_withdrawn: true,
        },
      })
      .then((value) => {
        if (value?.data?.data) {
          orderData.value = value.data.data;
          initialOrderStatus.value = orderData.value.order.status;
          updateData.financially_cleared = Boolean(orderData.value.order?.financially_cleared);
        }
        return value;
      }),
});

const sessionPricingPayload = computed(() => {
  // Note on Additional Fees and Add-ons:
  // No need to separate them here, backend filters them out correspondingly
  return {
    session_id: orderData.value?.order?.session_id,
    home_institution_id: orderData.value?.order?.home_institution_id,
    rooms: orderData.value?.order?.room_ids ?? [],
    internships: orderData.value?.order?.internship_ids ?? [],
    event_occurrences: orderData.value?.order?.event_occurrence_ids ?? [],
    addons: orderData.value?.order?.misc_product_ids ?? [],
    additional_fees: orderData.value?.order?.misc_product_ids ?? [],
    ...(hasDisplayStudentDiscountEnabled.value
      ? { special_pricing_override_entity_id: orderData.value?.order?.special_pricing_override_entity_id }
      : {}),
  };
});

const enableSessionPricing = computed(
  () => usePriceOverrides.value && typeof orderData.value?.order?.session_id === "string",
);

const { isLoading: sessionPricingLoading, data: sessionPricingData } = useGetSessionPricing({
  request: sessionPricingPayload,
  useQueryOptions: {
    staleTime: 0,
    enabled: enableSessionPricing,
  },
  get_active: ref(true),
});

const orderSessionId = computed(() => orderData.value?.order?.session_id);
const enableProgramSession = computed(() => !!orderData.value?.order?.session_id);
const enableOrdersDiff = computed(() => !!(orderData.value?.order && programSession.value?.items));
const sessionBasePriceTotal = computed(() =>
  formattedPriceFromCentsWithoutCents(programSession.value?.items?.base_price_in_cents),
);
const durationsDiff = computed(() => {
  return {
    session_duration: programSession?.value?.items?.session_travel_details?.[0]?.duration ?? 0,
    order_duration: orderData?.value?.order?.travel_duration ?? 0,
  };
});

const isLoading = computed(() => mutationOrder.isPending.value || isOrderLoading.value);

const { data: programSession } = useQuery({
  queryKey: ["programSession", orderSessionId.value],
  queryFn: () =>
    programSessionsService.getProgramSession({ id: orderSessionId.value }).then((val) => {
      return val?.data?.data || {};
    }),
  enabled: enableProgramSession,
});

const { data: ordersDiff } = useQuery({
  queryKey: orderPriceDiffQueryKey.value,
  queryFn: () =>
    useGetOrderSessionDiff({
      programSession: programSession.value.items,
      order: orderData.value.order,
      newProducts: [],
      forceRecalculate: true,
    }).then((value) => value || {}),
  enabled: enableOrdersDiff,
});

const mutationOrder = useMutation({
  mutationFn: (payload) =>
    ordersService.updateOrder({
      order_id: route.params?.orderId,
      payload,
      callingLocation: "OrderDetails",
    }),
  onSuccess: (response) => {
    if (orderData.value.order) {
      orderData.value.order.financially_cleared = Boolean(response?.data?.data?.order?.financially_cleared);
    }
    queryClient.invalidateQueries({
      queryKey: getOneQueryKey.value,
    });
    queryClient.invalidateQueries({
      queryKey: orderPriceDiffQueryKey.value,
    });
  },
  onError: () => {
    orderData.value.order.status = initialOrderStatus.value;
  },
});

const updateOrder = async (payload) => {
  const { order } = orderData.value;
  await mutationOrder.mutateAsync({ ...order, ...payload });
};

const submitChanges = async () => {
  const formioResourceId = store?.state?.currentUser?.formioAdvisorId;

  openPriceDiffModal.value = false;
  openOrderWithdrawnModal.value = false;
  disableButton.value = true;

  try {
    if (formioResourceId) {
      if (hasOrdersWriteFulfillPermission?.value) {
        const statusValidation = validateOrderStatusAllowsPaidConfirmedOrFulfilled(
          orderData.value.order,
          orderData.value.order.status,
        );
        if (!statusValidation.ok) {
          toast.open({
            message: statusValidation.message,
            type: "error",
            position: "bottom",
            duration: ERROR_TIMEOUT,
          });
          orderData.value.order.status = initialOrderStatus.value;
          return;
        }
        await updateOrder({
          financially_cleared: updateData.financially_cleared,
          update_invoice: hasInvoiceFeatureEnabled.value && orderData.value.order.status === ORDER_STATUS.fulfilled,
          formio_resource_id: formioResourceId,
        });
        // update the initial order status to the new status after updating the order
        initialOrderStatus.value = orderData.value.order.status;
      }

      toast.open({
        message: "Order Saved Successfully",
        type: "success",
        position: "bottom",
        duration: SUCCESS_TIMEOUT,
      });
    } else {
      toast.open({
        message: "Required Form.IO ID not found for your user",
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });

      datadogLogs.logger.warn("Attempting to fulfill order but student has no formio id", {
        order: orderData?.value?.order || {},
        user: store?.state?.currentUser,
      });
    }
  } catch (error) {
    const errorMessage = error.cause?.data?.details?.detail ?? ERROR_MESSAGE;
    datadogLogs.logger.error("Failed to update order", {
      error: errorMessage,
      order: orderData?.value?.order || {},
      user: store?.state?.currentUser,
    });
    toast.open({
      message: errorMessage,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  } finally {
    disableButton.value = false;
  }
};
const handleClosePriceDiffModal = () => {
  openPriceDiffModal.value = false;
};
const handleSaveButton = async () => {
  if (orderData.value?.order?.status === ORDER_STATUS.withdrawn) {
    openOrderWithdrawnModal.value = true;
  } else {
    openPriceDiffModal.value = true;
  }
};

const closeOrderWithdrawnModal = () => {
  openOrderWithdrawnModal.value = false;
};

const setHasInvoices = (value) => {
  hasInvoices.value = value;
};
</script>

<template>
  <div class="bg-blue-100 text-sm text-indigo-base">
    <PriceDifferenceModal
      :is-modal-open="openPriceDiffModal"
      :orders-diff="ordersDiff"
      :durations-diff="durationsDiff"
      @go-on="submitChanges"
      @close-modal="handleClosePriceDiffModal"
    ></PriceDifferenceModal>
    <OrderWithdrawnModal
      v-model="orderData"
      :modal-is-open="openOrderWithdrawnModal"
      @save-order="submitChanges"
      @close-modal="closeOrderWithdrawnModal"
    />
    <div v-if="!orderError && !isLoading && orderData?.order?.id && !sessionPricingLoading" class="mx-5">
      <div class="flex items-baseline justify-between">
        <BreadCrumb class="mt-10" />
      </div>
      <ExpandingSection
        field-label="Student Info"
        :input-classes="['!bg-white mt-3 mb-3']"
        :unmount="false"
        class="col-span-2"
      >
        <template #content>
          <StudentInfo
            v-model="updateData"
            :order-loading="isLoading"
            :data="orderData"
          />
        </template>
      </ExpandingSection>

      <ExpandingSection
        field-label="Invoice details"
        :input-classes="['!p-0 !border-white !bg-white mt-3 mb-3']"
        :unmount="false"
        class="col-span-2"
      >
        <template #content>
          <InvoiceSection
            :order-id="orderData?.order?.id"
            :application-id="orderData?.order?.application_id"
            @update:has-invoices="setHasInvoices"
          />
        </template>
      </ExpandingSection>

      <ExpandingSection
        :field-label="`Session Base Price Total When Applied: ${sessionBasePriceTotal}`"
        input-classes="!p-0 !border-white !bg-white mt-3 mb-3"
        :unmount="false"
        class="col-span-2"
      >
        <template #content>
          <SessionBasePrice :order-data="orderData?.order" />
        </template>
      </ExpandingSection>

      <ExpandingSection
        field-label="Order details"
        :input-classes="['!p-0 !border-white !bg-white mt-3 mb-3']"
        :unmount="false"
        class="col-span-2"
      >
        <template v-if="orderData?.order?.updated_at" #button>
          <p class="italic">
            Last updated:
            {{ formatDate(parseISO(orderData.order.updated_at), "MM/dd/yyyy") }}
          </p>
        </template>
        <template #content>
          <DetailSection
            v-model="orderData"
            :initial-order-status="initialOrderStatus"
            :program-session="programSession"
            :has-invoices="hasInvoices"
            :session-prices="sessionPricingData"
            @handle-save-button="handleSaveButton"
            @withdraw-order="openOrderWithdrawnModal = true"
          />
        </template>
      </ExpandingSection>

      <ExpandingSection
        field-label="Notes"
        :input-classes="['!p-0 !border-white !bg-white mt-3 mb-3']"
        :unmount="false"
        class="col-span-2"
      >
        <template #button>
          <button
            v-if="hasOrdersWritePermission || hasUsersAdvisePermission"
            class="flex justify-center items-center ml-4 text-sm font-bold bg-white text-teal-900 uppercase"
            @click="() => (openAddEditNoteModal = true)"
          >
            <PlusIcon /><span class="ml-2">New Note</span>
          </button>
        </template>
        <template #content>
          <NotesSection
            :order-data="orderData"
            :open-add-edit-note-modal="openAddEditNoteModal"
            @close-modal-add-edit-note-modal="
              () => (openAddEditNoteModal = false)
            "
          />
        </template>
      </ExpandingSection>
    </div>
    <ErrorPage
      v-else-if="!isLoading && orderError"
      class="relative h-65vh"
      message="Failed to load order detail data"
      disable-code
    />
    <Spinner v-else class="relative h-screen bg-blue-100" />
  </div>
</template>
